import { Box, Button, Flex, HStack, Text, VStack } from "@chakra-ui/react"
import { useTranslate } from "@pathwright/ux/src/components/lng/useTranslate"
import React, { memo, useEffect, useRef } from "react"

import { useKatex } from "@pathwright/utils/src/katex/useKatex"
import UserAvatar from "@pathwright/ux/src/components/avatar/UserAvatar"
import { Indicator } from "@pathwright/ux/src/components/button/IconButton"
import { useColorModeValue } from "@pathwright/ux/src/components/chakra/ui/color-mode"
import Link from "@pathwright/ux/src/components/link/Link"
import Pathicon from "@pathwright/ux/src/components/pathicon/Pathicon"
import Tooltip from "@pathwright/ux/src/components/tooltip/Tooltip"
import moment from "moment"
import { DiscussionResponseFragment } from "@pathwright/graphql-client/src/generated"
import RedactorContent from "../../redactor/RedactorContent"
import {
  NonArchivedDiscussionResponseFragment,
  useDiscussionContext
} from "../DiscussionProvider"
import DiscussionResponseDelete from "./DiscussionResponseDelete"
import DiscussionResponseForm from "./DiscussionResponseForm"
import ResponseListItemMenu from "./ResponseListItemMenu"
import useDiscussionResponsePermissions from "./useDiscussionResponsePermissions"

// Calculate the indent for a response based on its depth.
export function useResponseDepthIndent(response: DiscussionResponseFragment) {
  const rootResponseDepth = useDiscussionContext((c) => c.responses?.[0]?.depth)

  // The first response in the list will have the root depth, which may be 1
  // or, if we're scoped to a response, it will be the depth of that response.
  const rootDepth = rootResponseDepth ?? 1

  // We offset the depth by the root depth, so the first response in the list will have a depth of 0.
  const depth = response.depth - rootDepth

  // Note that 2rem maps to the width of the avatar, keeping a clean indent for
  // nested responses, aligning with parent response bodies.
  const indent = `calc(var(--chakra-spacing-4) * ${depth} + ${depth * 2}rem)`

  return indent
}

type ResponseListItemProps = {
  response: NonArchivedDiscussionResponseFragment
}

const ResponseListItem = memo(({ response }: ResponseListItemProps) => {
  const { t } = useTranslate()
  const cohortId = useDiscussionContext((c) => c.post?.cohort_id!)
  const rootResponse = useDiscussionContext((c) => c.response)
  const likeResponse = useDiscussionContext((c) => c.likeResponse)
  const unlikeResponse = useDiscussionContext((c) => c.unlikeResponse)
  const replyingTo = useDiscussionContext((c) => c.replyingTo)
  const setReplyingTo = useDiscussionContext((c) => c.setReplyingTo)
  const deletingResponse = useDiscussionContext((c) => c.deletingResponse)
  const deleteResponse = useDiscussionContext((c) => c.deleteResponse)
  const setDeletingResponse = useDiscussionContext((c) => c.setDeletingResponse)
  const permissions = useDiscussionResponsePermissions(cohortId, response)
  const ref = useRef<HTMLDivElement>(null)
  const bodyRef = useRef<HTMLDivElement>(null)

  // Optimistic responses have negative IDs, we can use this to detect them, treating
  // them as newly added responses, scrolling them into view on mount and highlighting them.
  const isOptimistic = response.id < 0

  useEffect(() => {
    if (isOptimistic && bodyRef.current) {
      // Scroll to the newly added optimistic response.
      bodyRef.current.scrollIntoView({ behavior: "smooth", block: "center" })
    }
  }, [])

  const depthIndent = useResponseDepthIndent(response)
  const optimisticBgColor = useColorModeValue("yellow.50", "yellow.800")

  const votePrompt = response.user_liked
    ? t("discussion.list_item.remove_vote")
    : t("discussion.list_item.upvote")

  // Check for unsaved response form state for the "Reply" button
  const hasUnsavedResponse = false

  useKatex(bodyRef, { contentKey: response.body })

  const handleReply = (e: React.MouseEvent) => {
    e.preventDefault()
    setReplyingTo(response)
  }

  const handleDelete = () => {
    deleteResponse?.(response)
    setDeletingResponse(null)
  }

  const handleCancelDelete = () => {
    setDeletingResponse(null)
  }

  const authorName = permissions.canViewFullName
    ? response.user.full_name
    : response.user.display_name

  // Already linked to a response? then remove that part from the relativeTo.
  const relativeTo = rootResponse
    ? `../../response/${response.id}/`
    : `response/${response.id}/`

  return (
    <Flex
      ref={ref}
      className={`response-list-item-view response-depth-${response.depth}`}
      data-depth={response.depth}
      id={response.id ? `response-${response.id}` : undefined}
      gap={2}
      pl={depthIndent}
      w="full"
      pos="relative"
    >
      {isOptimistic && (
        <Box
          className="response-optimistic-indicator"
          bg={optimisticBgColor}
          pos="absolute"
          // Insetting outside parent for a padding effect.
          inset="-2"
          left={`calc(${depthIndent} - var(--chakra-spacing-2))`}
          transition="background .3s"
          borderRadius="card"
        />
      )}
      {/* Avatar */}
      <Box className="response-who" flexShrink={0}>
        <UserAvatar size={["xs", "sm"]} user={response.user} />
      </Box>

      <VStack flex={1} align="stretch" gap={4} minW={0}>
        {/* Response content */}
        <VStack className="response-detail" flex={1} align="stretch" gap={1}>
          {/* Author info and admin menu */}
          <Flex
            className="response-info"
            justify="space-between"
            align="center"
            pos="relative"
          >
            <HStack gap={2} fontSize="sm">
              <Text
                as="span"
                className="response-by-line"
                fontWeight="semibold"
              >
                {authorName}
              </Text>

              {response.created_time && (
                <Text as="span" className="response-time" color="gray.500">
                  <Link relativeTo={relativeTo} className="response-link">
                    <Text as="time" fontSize="sm">
                      {moment(response.created_time).fromNow()}
                    </Text>
                  </Link>
                </Text>
              )}
            </HStack>

            {/* Admin menu - using existing React component */}
            {!isOptimistic && (
              <Box
                className="discussion-admin-menu-container"
                position="absolute"
                right="0"
              >
                <ResponseListItemMenu response={response} />
              </Box>
            )}
          </Flex>

          {/* Response body */}
          <Box position="relative">
            <RedactorContent
              ref={bodyRef}
              className="response-body brand-content content-reset"
              content={response.body}
              overflow="hidden"
            />
          </Box>

          {/* Response meta (votes and reply) */}
          <HStack className="response-meta" gap={2} fontSize="sm">
            {/* Voting */}
            <Tooltip
              // className="response-votes"
              title={votePrompt}
              fitContent
            >
              <HStack gap={1}>
                {/* TODO: this icon + count button pattern could be a good ux library candidate */}
                <Button
                  variant="ghost"
                  colorPalette={response.user_liked ? "brand" : "blackAlpha"}
                  aria-label={votePrompt}
                  onClick={() =>
                    response.user_liked
                      ? unlikeResponse(response)
                      : likeResponse(response)
                  }
                  borderRadius="full"
                  disabled={!permissions.canVote}
                  padding={1}
                  gap={0}
                >
                  {response.user_liked ? (
                    <Pathicon icon="heart-fill" />
                  ) : (
                    <Pathicon icon="heart" />
                  )}
                  {!!response.likes && (
                    <Text as="span" color="inherit">
                      {response.likes}
                    </Text>
                  )}
                </Button>
              </HStack>
            </Tooltip>

            {/* Reply button */}
            {permissions.canCreate && !replyingTo && (
              <Box pos="relative" pr={hasUnsavedResponse ? 2 : 0}>
                <Link asChild variant="underline">
                  <Button
                    variant="plain"
                    onClick={handleReply}
                    className="response-reply brand-color"
                    colorPalette="brand"
                    title={t("discussion.list_item.reply_to_user", {
                      user: response.user.first_name
                    })}
                    width="fit-content"
                    minW="unset"
                    p={0}
                  >
                    {t("discussion.list_item.reply_to_user", {
                      user: response.user.first_name
                    })}
                  </Button>
                </Link>
                {hasUnsavedResponse && <Indicator />}
              </Box>
            )}
          </HStack>
        </VStack>
        {replyingTo?.id === response.id && (
          <DiscussionResponseForm replyParent={replyingTo} />
        )}
        {deletingResponse?.id === response.id && (
          <DiscussionResponseDelete
            onDeleteResponse={handleDelete}
            onCancelDelete={handleCancelDelete}
          />
        )}
      </VStack>
    </Flex>
  )
})

export default ResponseListItem
